import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { ItemCategoryService } from './item-category.service';
import {
  BulkUploadComponent,
  excelDataAndDataTypeInterface
} from 'src/app/Shared Utilities/bulk-upload/bulk-upload.component';
import { UserLoginService } from 'src/app/login/userlogin.service';
import { UserAffiliationMappingService } from 'src/app/housing-society/Interface & Service/userAffiliationMapping/userAffiliationMapping.service';
import { SMUserAffiliationMappingReadByUserIdRequestDTO } from 'src/app/housing-society/Interface & Service/userAffiliationMapping/userAffiliationMapping.interface';
import { HospitalUserAffiliationService } from 'src/app/hospital/HospitalUserMapping/user-affiliation-mapping.service';
import {
  ItemCategoryDTO,
  ItemCategoryList,
  ItemCategoryCreateRequestDTO,
  ItemCategoryUpdateRequestDTO,
  ItemCategoryDeleteRequestDTO,
  ItemCategoryReadByIdRequestDTO
} from './item-category.interface';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-item-category',
  templateUrl: './item-category.component.html',
  styleUrls: ['./item-category.component.css']
})
export class ItemCategoryComponent implements OnInit {
  @Input() hideList: boolean = false;
  @Output() closed = new EventEmitter<void>();
  private bulkUploadService = inject(BulkUploadComponent);
  private toastr = inject(ToastrService);
  private User = inject(UserLoginService).User();
  private hospitalUserAffiliationMappingService = inject(HospitalUserAffiliationService);
  private schoolUserAffiliationMappingService = inject(UserAffiliationMappingService);
  private societyUserAffiliationMappingService = inject(UserAffiliationMappingService);

  // =========================================================
  // MODULE
  // =========================================================
  moduleName: string = '';
  moduleId: number = 0;
  isModuleIdLoading: boolean = false;
  moduleIdFetchAttempted: boolean = false;

  // =========================================================
  // STATE
  // =========================================================
  showForm: boolean = false;
  itemCategoryList: ItemCategoryDTO[] = [];
  parentCategoryList: ItemCategoryDTO[] = [];
  isLoading: boolean = false;
  isEditMode: boolean = false;
  selectedItemCategoryId: number = 0;

  pageNo: number = 1;
  pageSize: number = 25;
  totalRecords: number = 0;

  showDeleteConfirmation: boolean = false;
  deleteItemCategoryId: number = 0;

  searchText: string = '';

  // =========================================================
  // DROPDOWNS
  // =========================================================
  inventoryTypes: string[] = [
    'Medicine',
    'Medical Consumable',
    'Surgical Consumable',
    'Implant',
    'Medical Device',
    'Lab Reagent',
    'General Consumable'
  ];

  storageClasses: string[] = [
    'Ambient',
    'Cold Chain (2–8°C)',
    'Frozen',
    'Controlled Cabinet'
  ];

  // =========================================================
  // FORM MODEL
  // =========================================================
  itemCategory: any = this.buildEmptyCategory();

  constructor(private itemCategoryService: ItemCategoryService) {}

  ngOnInit(): void {
    this.moduleName = this.getModuleNameFromUrl();
    this.initializeComponent();
  }

  async initializeComponent(): Promise<void> {
    await this.fetchModuleIdFromUserAffiliation();
    this.readAllItemCategories();
  }

  private buildEmptyCategory(): any {
    return {
      itemCategoryId: 0,
      moduleName: '',
      moduleId: 0,
      parentCategoryId: 0,
      categoryCode: '',
      categoryName: '',
      description: '',
      isActive: 1,
      actionUser: 'SYSTEM',

      // extended classification/storage
      inventoryType: '',
      storageClass: ''
    };
  }

  // =========================================================
  // MODULE RESOLUTION
  // =========================================================
  getModuleNameFromUrl(): string {
    const url = window.location.pathname.toLowerCase();
    if (url.startsWith('/hospital/')) return 'Hospital';
    if (url.startsWith('/school/'))   return 'School';
    if (url.startsWith('/society/'))  return 'Society';
    return '';
  }

  get maxPage(): number {
    return Math.ceil(this.totalRecords / this.pageSize);
  }

  fetchModuleIdFromUserAffiliation(): Promise<void> {
    const userId = this.User?.userId;
    if (!userId) {
      this.moduleIdFetchAttempted = true;
      return Promise.resolve();
    }

    this.isModuleIdLoading = true;
    this.moduleIdFetchAttempted = true;

    return new Promise((resolve) => {

      if (this.moduleName === 'Hospital') {
        const request: any = { userId };
        this.hospitalUserAffiliationMappingService
          .HospitalUserAffiliationReadByUserId(request)
          .subscribe({
            next: (response: any) => {
              const affiliation = response?.items?.[0];
              this.moduleId = affiliation?.hospitalId ?? this.extractIdFromUrl();
              this.itemCategory.moduleId = this.moduleId;
              this.itemCategory.moduleName = this.moduleName;
              this.isModuleIdLoading = false;
              resolve();
            },
            error: () => {
              this.moduleId = this.extractIdFromUrl();
              this.itemCategory.moduleId = this.moduleId;
              this.itemCategory.moduleName = this.moduleName;
              this.isModuleIdLoading = false;
              resolve();
            }
          });
      } else if (this.moduleName === 'School' || this.moduleName === 'Society') {
        const request: SMUserAffiliationMappingReadByUserIdRequestDTO = { userId };
        const service = this.moduleName === 'School'
          ? this.schoolUserAffiliationMappingService
          : this.societyUserAffiliationMappingService;

        service.SmUserAffiliationMapping_ReadByUserId(request).subscribe({
          next: (response: any) => {
            const a = response?.items?.[0] || {};
            this.moduleId =
              a.societyId || a.SocietyId ||
              a.schoolId  || a.SchoolId  ||
              this.extractIdFromUrl();
            this.itemCategory.moduleId = this.moduleId;
            this.itemCategory.moduleName = this.moduleName;
            this.isModuleIdLoading = false;
            resolve();
          },
          error: () => {
            this.moduleId = this.extractIdFromUrl();
            this.itemCategory.moduleId = this.moduleId;
            this.itemCategory.moduleName = this.moduleName;
            this.isModuleIdLoading = false;
            resolve();
          }
        });
      } else {
        this.moduleId = this.extractIdFromUrl();
        this.itemCategory.moduleId = this.moduleId;
        this.itemCategory.moduleName = this.moduleName;
        this.isModuleIdLoading = false;
        resolve();
      }
    });
  }

  private extractIdFromUrl(): number {
    const url = window.location.pathname;
    const matches = url.match(/\/(\d+)/);
    if (matches && matches[1]) return parseInt(matches[1]);
    return 0;
  }

  // =========================================================
  // PARENT OPTIONS
  // =========================================================
  getParentOptions(): ItemCategoryDTO[] {
    return this.parentCategoryList.filter(
      p => !this.isEditMode || p.itemCategoryId !== this.selectedItemCategoryId
    );
  }

  // =========================================================
  // SEARCH
  // =========================================================
  onSearch(): void {
    const search = this.searchText.trim().toLowerCase();
    if (!search) {
      this.readAllItemCategories();
      return;
    }
    this.itemCategoryList = this.itemCategoryList.filter(item =>
      item.categoryName?.toLowerCase().includes(search) ||
      item.categoryCode?.toLowerCase().includes(search) ||
      item.description?.toLowerCase().includes(search)
    );
    this.totalRecords = this.itemCategoryList.length;
    this.pageNo = 1;
  }

  // =========================================================
  // VALIDATION
  // =========================================================
  isFormValid(): boolean {
    return !!(this.itemCategory.categoryName &&
              this.itemCategory.categoryName.trim() !== '');
  }

  // =========================================================
  // READ ALL
  // =========================================================
  readAllItemCategories(): void {
    this.isLoading = true;

    if (this.moduleId === 0 && !this.isModuleIdLoading) {
      this.fetchModuleIdFromUserAffiliation().then(() => {
        this.doReadAllItemCategories();
      });
    } else {
      this.doReadAllItemCategories();
    }
  }

  private doReadAllItemCategories(): void {
    this.itemCategoryService.ItemCategory_ReadAll().subscribe({
      next: (response: ItemCategoryList) => {
        const all = response?.items || [];

        this.itemCategoryList = all.filter(
          x => x.moduleId === this.moduleId &&
               x.moduleName === this.moduleName
        );

        this.parentCategoryList = this.itemCategoryList.filter(
          x => !x.parentCategoryId || x.parentCategoryId === 0
        );

        this.totalRecords = this.itemCategoryList.length;
        this.isLoading = false;
      },
      error: (error: any) => {
        console.error('Error while reading Item Categories:', error);
        this.itemCategoryList = [];
        this.parentCategoryList = [];
        this.totalRecords = 0;
        this.isLoading = false;
      }
    });
  }

  // =========================================================
  // OPEN ADD / EDIT
  // =========================================================
  openAdd(): void {
    if (this.moduleId === 0) {
      this.fetchModuleIdFromUserAffiliation().then(() => this.doOpenAdd());
    } else {
      this.doOpenAdd();
    }
  }

  private doOpenAdd(): void {
    this.resetForm();
    this.itemCategory.parentCategoryId = 0;
    this.itemCategory.moduleId = this.moduleId;
    this.itemCategory.moduleName = this.moduleName;
    this.isEditMode = false;
    this.showForm = true;
  }

  openEdit(item: ItemCategoryDTO): void {
    if (this.moduleId === 0) {
      this.fetchModuleIdFromUserAffiliation().then(() => this.doOpenEdit(item));
    } else {
      this.doOpenEdit(item);
    }
  }

  private doOpenEdit(item: ItemCategoryDTO): void {
    this.isEditMode = true;
    this.showForm = true;
    this.selectedItemCategoryId = item.itemCategoryId;

    this.itemCategory = {
      itemCategoryId: item.itemCategoryId,
      moduleName: this.moduleName,
      moduleId: this.moduleId,
      parentCategoryId: item.parentCategoryId ?? 0,
      categoryCode: item.categoryCode || '',
      categoryName: item.categoryName || '',
      description: item.description || '',
      isActive: item.isActive ?? 1,
      actionUser: 'SYSTEM',

      inventoryType: item.otherDetail1 || '',
      storageClass: item.otherDetail2 || ''
    };
  }

  // =========================================================
  // CREATE
  // =========================================================
  createItemCategory(): void {
    if (!this.moduleId || this.moduleId === 0) {
      this.fetchModuleIdFromUserAffiliation().then(() => {
        if (this.moduleId && this.moduleId !== 0) this.doCreateItemCategory();
        else this.toastr.error('Could not load module ID. Please refresh and try again.');
      });
      return;
    }
    this.doCreateItemCategory();
  }

  private doCreateItemCategory(): void {
    if (!this.itemCategory.categoryName ||
        this.itemCategory.categoryName.trim() === '') {
      this.toastr.error('Category Name is required');
      return;
    }

    const request: ItemCategoryCreateRequestDTO = {
      moduleName: this.moduleName,
      moduleId: this.moduleId,
      parentCategoryId: this.itemCategory.parentCategoryId || 0,
      categoryCode: this.generateCategoryCode(
    this.itemCategory.categoryName
  ),
      categoryName: this.itemCategory.categoryName,
      description: this.itemCategory.description || '',
      displayOrder: 0,

      otherDetail1: this.itemCategory.inventoryType || '',
      otherDetail2: this.itemCategory.storageClass || '',
      otherDetail3: '',
      otherDetail4: '',
      otherDetail5: '',
      otherDetail6: '',

      actionUser: 'SYSTEM'
    };

    this.itemCategoryService.ItemCategory_Create(request).subscribe({
      next: () => {
        this.toastr.success('Category created successfully');
        this.resetForm();
        this.readAllItemCategories();
      },
      error: () => this.toastr.error('Failed to create category')
    });
  }

  // =========================================================
  // UPDATE
  // =========================================================
  updateItemCategory(): void {
    if (!this.moduleId || this.moduleId === 0) {
      this.fetchModuleIdFromUserAffiliation().then(() => {
        if (this.moduleId && this.moduleId !== 0) this.doUpdateItemCategory();
        else this.toastr.error('Could not load module ID. Please refresh and try again.');
      });
      return;
    }
    this.doUpdateItemCategory();
  }

  private doUpdateItemCategory(): void {
    if (!this.itemCategory.categoryName ||
        this.itemCategory.categoryName.trim() === '') {
      this.toastr.error('Category Name is required');
      return;
    }

    const request: ItemCategoryUpdateRequestDTO = {
      itemCategoryId: this.itemCategory.itemCategoryId,
      moduleName: this.moduleName,
      moduleId: this.moduleId,
      parentCategoryId: this.itemCategory.parentCategoryId || 0,
      categoryCode: this.itemCategory.categoryCode || this.itemCategory.categoryName,
      categoryName: this.itemCategory.categoryName,
      description: this.itemCategory.description || '',
      displayOrder: 0,

      otherDetail1: this.itemCategory.inventoryType || '',
      otherDetail2: this.itemCategory.storageClass || '',
      otherDetail3: '',
      otherDetail4: '',
      otherDetail5: '',
      otherDetail6: '',

      isActive: this.itemCategory.isActive ?? 1,
      actionUser: 'SYSTEM'
    };

    this.itemCategoryService.ItemCategory_Update(request).subscribe({
      next: () => {
        this.toastr.success('Category updated successfully');
        this.resetForm();
        this.readAllItemCategories();
      },
      error: () => this.toastr.error('Failed to update category')
    });
  }

  // =========================================================
  // SAVE
  // =========================================================
  saveItemCategory(): void {
    if (!this.isFormValid()) {
      this.toastr.error('Category Name is required');
      return;
    }
    if (this.isEditMode) this.updateItemCategory();
    else this.createItemCategory();
  }

  // =========================================================
  // DELETE
  // =========================================================
  confirmDelete(item: ItemCategoryDTO): void {
    this.deleteItemCategoryId = item.itemCategoryId;
    this.showDeleteConfirmation = true;
  }

  deleteItemCategory(itemCategoryId: number): void {
    const request: ItemCategoryDeleteRequestDTO = {
      itemCategoryId,
      actionUser: 'SYSTEM'
    };
    this.itemCategoryService.ItemCategory_Delete(request).subscribe({
      next: () => {
        this.toastr.success('Category deleted successfully');
        this.showDeleteConfirmation = false;
        this.deleteItemCategoryId = 0;
        this.readAllItemCategories();
      },
      error: () => this.toastr.error('Failed to delete category')
    });
  }

  cancelDelete(): void {
    this.showDeleteConfirmation = false;
    this.deleteItemCategoryId = 0;
  }

  // =========================================================
  // EXCEL
  // =========================================================
  handleDownloadSampleExcel(): void {
    const anchor = document.createElement('a');
    anchor.href = './assets/sampleExcelFiles/Inventory/sample_addItemCategory.xlsx';
    anchor.download = 'sample-excel';
    anchor.click();
  }

  async handleExcelFileUpload(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file: File = input.files[0];

    if (!this.moduleId || this.moduleId === 0) {
      await this.fetchModuleIdFromUserAffiliation();
      if (!this.moduleId || this.moduleId === 0) {
        this.toastr.error('Could not load module ID. Please refresh and try again.');
        input.value = '';
        return;
      }
    }

    const requireEntriesList: excelDataAndDataTypeInterface[] = [
      
      { name: 'categoryName',   dataType: 'varchar 200' },
      { name: 'description',    dataType: 'varchar 500', required: false },
      { name: 'inventoryType',  dataType: 'varchar 100', required: false },
      { name: 'storageClass',   dataType: 'varchar 100', required: false }
    ];

    const fileData = await this.bulkUploadService.fileDataValidationAndObjectList(
      requireEntriesList,
      file
    );

    if (typeof fileData === 'string') {
      this.toastr.error(fileData);
      input.value = '';
      return;
    }

    if (!fileData.tableData || fileData.tableData.length === 0) {
      this.toastr.error('No data found in Excel file');
      input.value = '';
      return;
    }

    this.isLoading = true;
    let successCount = 0;
    let errorCount = 0;

    for (const row of fileData.tableData) {
      const request: ItemCategoryCreateRequestDTO = {
        moduleName: this.moduleName,
        moduleId: this.moduleId,
        parentCategoryId: 0,
        categoryCode: row.categoryCode || row.categoryName || '',
        categoryName: row.categoryName || '',
        description: row.description || '',
        displayOrder: 0,

        otherDetail1: row.inventoryType || '',
        otherDetail2: row.storageClass || '',
        otherDetail3: '',
        otherDetail4: '',
        otherDetail5: '',
        otherDetail6: '',

        actionUser: 'SYSTEM'
      };

      this.itemCategoryService.ItemCategory_Create(request).subscribe({
        next: () => {
          successCount++;
          if (successCount + errorCount === fileData.tableData.length) {
            this.isLoading = false;
            if (successCount > 0) this.toastr.success(`Successfully uploaded ${successCount} categories`);
            if (errorCount > 0)    this.toastr.warning(`${errorCount} categories failed to upload`);
            this.readAllItemCategories();
            input.value = '';
          }
        },
        error: () => {
          errorCount++;
          if (successCount + errorCount === fileData.tableData.length) {
            this.isLoading = false;
            if (successCount > 0) this.toastr.success(`Successfully uploaded ${successCount} categories`);
            if (errorCount > 0)    this.toastr.warning(`${errorCount} categories failed to upload`);
            this.readAllItemCategories();
            input.value = '';
          }
        }
      });
    }

    if (fileData.tableData.length === 0) {
      this.isLoading = false;
      input.value = '';
    }
  }

  // =========================================================
  // RESET / PAGINATION
  // =========================================================
  resetForm(): void {
    this.showForm = false;
    this.isEditMode = false;
    this.selectedItemCategoryId = 0;
    this.itemCategory = this.buildEmptyCategory();
    this.itemCategory.moduleName = this.moduleName;
    this.itemCategory.moduleId = this.moduleId;
    
  }
  closeForm(): void {
  this.showForm = false;
  this.isEditMode = false;
  this.selectedItemCategoryId = 0;
  this.itemCategory = this.buildEmptyCategory();

  this.itemCategory.moduleName = this.moduleName;
  this.itemCategory.moduleId = this.moduleId;

  this.closed.emit();
}
  nextPage(): void {
    if (this.pageNo < this.maxPage) {
      this.pageNo++;
      this.readAllItemCategories();
    }
  }

  previousPage(): void {
    if (this.pageNo > 1) {
      this.pageNo--;
      this.readAllItemCategories();
    }
  }
  private generateCategoryCode(categoryName: string): string {
  if (!categoryName?.trim()) return '';

  return categoryName
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
}